// src/components/Quests.js
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDailyQuests } from "../hooks/useDailyQuests";
import { getCurrentQuestDate } from "../utils/dateUtils";

import AddQuestModal from "./AddQuestModal";
import QuestTimer from "./QuestTimer";

import {
  claimDailyQuestReward,
  startDurationQuest,
  unclaimDailyQuestReward,
} from "../api/systemApi";
import { playSystemSound } from "../audio/soundManager";
import {
  formatCountdownDuration,
  getQuestCompletionText,
  getQuestDeadlineLabel,
  getQuestDurationLabel,
  getDurationQuestRemainingMs,
  getDurationQuestTargetDate,
  isQuestExpired,
  isDurationQuest,
  isDurationQuestReadyToComplete,
  hasDurationQuestStarted,
  formatQuestTime,
} from "../utils/questUtils";

import { FaThumbtack } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";

const formatCategoryLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const getQuestStatus = (quest, referenceDate = new Date()) => {
  if (quest.status === "failed") return { label: "Failed", tone: "failed" };
  if (isDurationQuest(quest) && hasDurationQuestStarted(quest) && !quest.claimed) {
    if (isDurationQuestReadyToComplete(quest, referenceDate)) {
      return { label: "Timer complete", tone: "ready" };
    }
    return { label: "Active", tone: "active" };
  }
  if (quest.completed === true || quest.status === "completed") {
    return {
      label: quest.claimed ? "Completed" : "Ready to claim",
      tone: quest.claimed ? "done" : "ready",
    };
  }
  return { label: "Pending", tone: "pending" };
};

const Quests = ({ userLevel, user, userProfile, onProfileUpdate, onQuestStateChange }) => {
  const today = getCurrentQuestDate();

  const [showAdd, setShowAdd] = useState(false);
  const [editQuest, setEditQuest] = useState(null);
  const [activeQuestId, setActiveQuestId] = useState(null);
  const [recurringQuestId, setRecurringQuestId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const autoFailingQuestIds = useRef(new Set());

  const {
    quests,
    loading,
    addQuest,
    updateQuest,
    deleteQuest,
    replaceQuests,
  } = useDailyQuests(user?.uid, today);

  useEffect(() => {
    setActiveQuestId(null);
    setRecurringQuestId(null);
    setFeedback(null);
  }, [today, user?.uid]);

  useEffect(() => {
    if (typeof onQuestStateChange === "function") {
      onQuestStateChange(Array.isArray(quests) ? quests : []);
    }
  }, [onQuestStateChange, quests]);

  useEffect(() => {
    const hasActiveDurationQuest = quests.some(
      (quest) =>
        isDurationQuest(quest) &&
        hasDurationQuestStarted(quest) &&
        !quest.claimed &&
        quest.status !== "failed"
    );

    if (!hasActiveDurationQuest) return undefined;

    const timerId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [quests]);

  const markQuestFailed = useCallback(
    async (quest) => {
      if (!user?.uid || !quest || autoFailingQuestIds.current.has(quest.id)) {
        return false;
      }

      autoFailingQuestIds.current.add(quest.id);
      try {
        await updateQuest(quest.id, {
          status: "failed",
          completed: false,
          claimed: false,
          completedAt: null,
          failedAt: new Date().toISOString(),
        });

        setFeedback({
          tone: "error",
          label: "[SYSTEM FAILURE]",
          text: quest.deadlineTime
            ? `${quest.title} missed its cutoff at ${formatQuestTime(quest.deadlineTime)} and was marked failed.`
            : `${quest.title} was marked failed by the System.`,
        });
        playSystemSound("questFailure");

        return true;
      } catch (error) {
        console.error("Failed to mark quest as failed:", error);
        setFeedback({
          tone: "error",
          label: "[SYSTEM ERROR]",
          text: "The System could not record the missed cutoff. Refresh and try again.",
        });
        return false;
      } finally {
        autoFailingQuestIds.current.delete(quest.id);
      }
    },
    [updateQuest, user?.uid]
  );

  useEffect(() => {
    if (!user?.uid || !quests.length) return undefined;

    let cancelled = false;

    const checkExpiredQuests = async () => {
      const expiredQuests = quests.filter((quest) =>
        isQuestExpired(quest, new Date(), today, userProfile?.wakeTime)
      );
      for (const quest of expiredQuests) {
        if (cancelled) return;
        await markQuestFailed(quest);
      }
    };

    checkExpiredQuests();
    const intervalId = window.setInterval(checkExpiredQuests, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [quests, markQuestFailed, user?.uid]);

  const finishAndClaim = async (quest) => {
    if (!user?.uid || activeQuestId) return;

    if (isQuestExpired(quest, new Date(), today, userProfile?.wakeTime)) {
      await markQuestFailed(quest);
      return;
    }

    setActiveQuestId(quest.id);
    setFeedback(null);

    try {
      const result = await claimDailyQuestReward(today, quest.id);
      replaceQuests(result.quests);
      if (result.profile && typeof onProfileUpdate === "function") {
        onProfileUpdate(result.profile);
      }
      playSystemSound("questComplete");
    } catch (error) {
      console.error("Failed to finish quest:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: "Quest completion could not be recorded. Try again.",
      });
    } finally {
      setActiveQuestId(null);
    }
  };

  const startTimedQuest = async (quest) => {
    if (!user?.uid || activeQuestId) return;

    if (isQuestExpired(quest, new Date(), today, userProfile?.wakeTime)) {
      await markQuestFailed(quest);
      return;
    }

    setActiveQuestId(quest.id);
    setFeedback(null);

    try {
      const result = await startDurationQuest(today, quest.id);
      replaceQuests(result.quests);
      setFeedback({
        tone: "info",
        label: "[SYSTEM TIMER]",
        text: `${quest.title} is now running. The System is tracking this block.`,
      });
      playSystemSound("questStart");
    } catch (error) {
      console.error("Failed to start duration quest:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: error?.message || "The System could not start this timed quest.",
      });
    } finally {
      setActiveQuestId(null);
    }
  };

  const undoClaim = async (quest) => {
    if (!user?.uid || activeQuestId) return;

    setActiveQuestId(quest.id);
    setFeedback(null);

    try {
      const result = await unclaimDailyQuestReward(today, quest.id);
      replaceQuests(result.quests);
      if (result.profile && typeof onProfileUpdate === "function") {
        onProfileUpdate(result.profile);
      }
      playSystemSound("questAction");
    } catch (error) {
      console.error("Failed to unclaim quest:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: "The XP rollback failed. Try again.",
      });
    } finally {
      setActiveQuestId(null);
    }
  };

  const handleDelete = async (questId) => {
    try {
      await deleteQuest(questId);
      playSystemSound("questAction");
    } catch (error) {
      console.error("Failed to delete quest:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: "Quest deletion failed. Try again.",
      });
    }
  };

  const handleToggleRecurring = async (quest) => {
    if (
      !quest ||
      !user?.uid ||
      quest.isPenalty ||
      quest.generatedBySystem ||
      recurringQuestId
    ) {
      return;
    }

    setRecurringQuestId(quest.id);
    setFeedback(null);

    try {
      await updateQuest(quest.id, { fixed: !quest.fixed });
      setFeedback({
        tone: "info",
        label: "[SYSTEM UPDATE]",
        text: quest.fixed
          ? `${quest.title} was released from the recurring queue.`
          : `${quest.title} was added to the recurring queue.`,
      });
      playSystemSound("questAction");
    } catch (error) {
      console.error("Failed to toggle recurring quest:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: "Recurring status could not be updated. Try again.",
      });
    } finally {
      setRecurringQuestId(null);
    }
  };

  return (
    <div className="quests-card">
      <h4>Daily Quests</h4>

      <QuestTimer quests={quests} questDate={today} wakeTime={userProfile?.wakeTime} />

      {feedback && (
        <div className={`system-feedback system-feedback--${feedback.tone}`}>
          <div className="system-feedback__label">{feedback.label}</div>
          <div className="system-feedback__text">{feedback.text}</div>
        </div>
      )}

      <div className="quest-actions">
        <button className="login-btn quest-action-btn" onClick={() => setShowAdd(true)}>
          + Add Quest
        </button>
      </div>

      <AddQuestModal
        show={showAdd}
        userLevel={userLevel}
        onClose={() => setShowAdd(false)}
        onAdd={addQuest}
      />

      {editQuest && (
        <AddQuestModal
          show={!!editQuest}
          userLevel={userLevel}
          onClose={() => setEditQuest(null)}
          onAdd={(updatedData) => {
            updateQuest(editQuest.id, updatedData);
            setEditQuest(null);
          }}
          initialData={editQuest}
          isEdit={true}
        />
      )}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <ul className="quest-list">
          {quests.map((quest) => {
            const referenceDate = new Date(nowTs);
            const isCompleted = quest.completed === true || quest.status === "completed";
            const isClaimed = quest.claimed === true;
            const isFailed = quest.status === "failed";
            const isBusy = activeQuestId === quest.id;
            const isRecurringBusy = recurringQuestId === quest.id;
            const status = getQuestStatus(quest, referenceDate);
            const deadlineLabel = getQuestDeadlineLabel(quest);
            const durationLabel = getQuestDurationLabel(quest);
            const completionText = getQuestCompletionText(quest);
            const categoryLabel = quest.category ? formatCategoryLabel(quest.category) : "";
            const durationQuest = isDurationQuest(quest);
            const durationStarted = hasDurationQuestStarted(quest);
            const durationReady = isDurationQuestReadyToComplete(quest, referenceDate);
            const remainingMs = getDurationQuestRemainingMs(quest, referenceDate);
            const durationTargetDate = getDurationQuestTargetDate(quest);
            const canShowStartButton =
              durationQuest && !durationStarted && !isCompleted && !isClaimed && !isFailed;
            const canShowCompleteButton =
              durationQuest && durationStarted && durationReady && !isCompleted && !isClaimed && !isFailed;
            const showDefaultCompleteButton =
              (!durationQuest && (!isCompleted || !isClaimed) && !isFailed) ||
              (durationQuest && canShowCompleteButton);

            return (
              <li
                key={quest.id}
                className={[
                  "quest-card",
                  quest.isPenalty ? "quest-card--penalty" : "",
                  isFailed ? "quest-card--failed" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="quest-card__header">
                  <div className="quest-card__heading">
                    <div className="quest-card__title-row">
                      <h5 className="quest-card__title">
                        {quest.title}
                        {quest.fixed && (
                          <span className="quest-card__fixed" title="Fixed quest (repeats daily)">
                            <FaThumbtack />
                          </span>
                        )}
                      </h5>
                      <span className="quest-card__xp">{quest.xp} XP</span>
                    </div>

                    <div className="quest-card__chips">
                      {categoryLabel && (
                        <span className="quest-card__chip">{categoryLabel}</span>
                      )}
                      {deadlineLabel && (
                        <span className="quest-card__chip quest-card__chip--deadline">
                          {deadlineLabel}
                        </span>
                      )}
                      {durationLabel && (
                        <span className="quest-card__chip quest-card__chip--duration">
                          {durationLabel}
                        </span>
                      )}
                      {quest.isPenalty && (
                        <span className="quest-card__chip quest-card__chip--penalty">
                          [SYSTEM MANDATE]
                        </span>
                      )}
                    </div>

                    {quest.isPenalty && (
                      <div className="quest-card__penalty-copy">
                        Issued by the System. Non-completion will be penalized.
                      </div>
                    )}
                  </div>

                  <span className={`quest-card__status quest-card__status--${status.tone}`}>
                    {status.label}
                  </span>
                </div>

                {quest.desc && <p className="quest-card__desc">{quest.desc}</p>}

                {completionText && (
                  <div className="quest-card__rule">
                    <span className="quest-card__rule-label">Complete when</span>
                    <strong className="quest-card__rule-text">{completionText}</strong>
                  </div>
                )}

                {durationQuest && durationStarted && !isCompleted && !isFailed && (
                  <div className="quest-card__rule quest-card__rule--timer">
                    <span className="quest-card__rule-label">System timer</span>
                    <strong className="quest-card__rule-text">
                      {durationReady
                        ? "Timer complete. Confirm completion now."
                        : `In progress — ${formatCountdownDuration(remainingMs)} remaining${
                            durationTargetDate
                              ? ` (ends ${durationTargetDate.toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit",
                                })})`
                              : ""
                          }`}
                    </strong>
                  </div>
                )}

                {quest.penaltyQuest && !quest.isPenalty && (
                  <div className="quest-card__penalty">
                    <span className="quest-card__rule-label">Penalty quest</span>
                    <strong className="quest-card__rule-text">{quest.penaltyQuest}</strong>
                  </div>
                )}

                <div className="quest-card__actions">
                  {canShowStartButton && (
                    <button
                      className="login-btn quest-card__action-btn quest-card__action-btn--start"
                      onClick={() => startTimedQuest(quest)}
                      title="Start Quest"
                      disabled={isBusy || isRecurringBusy}
                    >
                      {isBusy ? "Starting..." : "Start Quest"}
                    </button>
                  )}

                  {durationQuest && durationStarted && !durationReady && !isCompleted && !isFailed && (
                    <button
                      className="logout-btn quest-card__action-btn quest-card__action-btn--timer"
                      disabled
                      title="The System timer is still running"
                    >
                      {`In Progress ${formatCountdownDuration(remainingMs)}`}
                    </button>
                  )}

                  {showDefaultCompleteButton && (
                    <button
                      className="login-btn quest-card__action-btn"
                      onClick={() => finishAndClaim(quest)}
                      title={
                        durationQuest
                          ? "Complete Quest"
                          : isCompleted
                            ? "Claim XP"
                            : "Finish Quest"
                      }
                      disabled={isBusy || isRecurringBusy}
                    >
                      {isBusy
                        ? "Working..."
                        : durationQuest
                          ? "Complete Quest"
                          : isCompleted
                            ? "Claim XP"
                            : "Finish Quest"}
                    </button>
                  )}

                  {!quest.isPenalty && !quest.generatedBySystem && (
                    <button
                      className={`logout-btn quest-card__action-btn quest-card__action-btn--recurring ${quest.fixed ? "quest-card__action-btn--recurring-active" : ""}`}
                      onClick={() => handleToggleRecurring(quest)}
                      title={quest.fixed ? "Stop repeating this quest daily" : "Repeat this quest every day"}
                      disabled={isBusy || isRecurringBusy}
                    >
                      <FaThumbtack />
                      {isRecurringBusy
                        ? "Updating..."
                        : quest.fixed
                          ? "Disable Daily Repeat"
                          : "Repeat Every Day"}
                    </button>
                  )}

                  <button
                    className="logout-btn quest-card__action-btn"
                    onClick={() => handleDelete(quest.id)}
                    disabled={isBusy || isRecurringBusy}
                  >
                    Delete
                  </button>

                  {!quest.isPenalty && (
                    <button
                      className="login-btn quest-card__action-btn quest-card__action-btn--ghost"
                      title="Edit Quest"
                      onClick={() => setEditQuest(quest)}
                      disabled={isBusy || isRecurringBusy}
                    >
                      <FiEdit2 /> Edit
                    </button>
                  )}
                </div>

                {isCompleted && isClaimed && (
                  <div className="quest-card__complete-row">
                    <span className="quest-card__complete-text">✔ Completed</span>
                    <button
                      className="logout-btn quest-card__undo-btn"
                      onClick={() => undoClaim(quest)}
                      disabled={isBusy}
                    >
                      Unclaim
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Quests;
